import { useTranslate } from "@refinedev/core";
import { Flex, Select, SelectProps, Typography } from "antd";
import {
  ClassicFilterIcon,
  LandscapeFilterIcon,
  PortraitFilterIcon,
  SquareFilterIcon,
  TabletFilterIcon,
} from "components/icons/";
import { AspectRatio } from "pages/media/types/";
import { useMemo } from "react";

export const AspectRatioValue = {
  Desktop: "desktop",
  Mobile: "mobile",
  Tablet: "tablet",
  Square: "square",
  Classic: "classic",
} as const satisfies Record<string, AspectRatio>;

export type Orientation =
  | (typeof AspectRatioValue)[keyof typeof AspectRatioValue]
  | "all";

export const OrientationSelect = ({
  onChange,
  value,
  includeAll = true,
  showLabel = true,
  type = "default",
  ...selectProps
}: SelectProps & {
  onChange: (orientation: Orientation) => void;
  value: Orientation;
  includeAll?: boolean;
  showLabel?: boolean;
  type?: "extended" | "default";
}) => {
  const t = useTranslate();
  const getIconByOrientation = (value: string) => {
    switch (value) {
      case AspectRatioValue.Square:
        return <SquareFilterIcon />;
      case AspectRatioValue.Desktop:
        return <LandscapeFilterIcon />;
      case AspectRatioValue.Mobile:
        return <PortraitFilterIcon />;
      case AspectRatioValue.Classic:
        return <ClassicFilterIcon />;
      case AspectRatioValue.Tablet:
        return <TabletFilterIcon />;
      default:
        return null;
    }
  };
  const options = useMemo(() => {
    if (type === "extended") {
      return Object.values(AspectRatioValue);
    }
    return Object.values(AspectRatioValue).filter(
      (value) => value === "desktop" || value === "mobile"
    );
  }, [type]);

  return (
    <Select
      options={[...options, "all"]
        .filter((value) => includeAll || value !== "all")
        .map((value) => ({
          label: (
            <Flex
              align="center"
              gap={5}
              justify="space-between"
              style={{ height: 35 }}
            >
              {(showLabel || (value === "all" && includeAll)) && (
                <Typography.Text>
                  {t(`asset_libraries.orientationFilter.${value}`)}
                </Typography.Text>
              )}
              <Flex align="center">{getIconByOrientation(value)}</Flex>
            </Flex>
          ),
          value,
        }))}
      onChange={onChange}
      value={value}
      {...selectProps}
    />
  );
};
